import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";
import { useTripAnalytics, useTripById } from "@/api/trips";
import { getOrderRecipientInfo } from "@/lib/safe-access";
import { Package, MapPin } from "lucide-react";
import { TripProgress } from "./trip-progress";

interface TripDetailsDialogProps {
  tripId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TripDetailsDialog({
  tripId,
  open,
  onOpenChange,
}: TripDetailsDialogProps) {
  const { data, isLoading } = useTripAnalytics(open ? tripId : null);
  const progressQuery = useTripById(open ? (tripId ?? "") : "");

  // Early return if dialog is closed or no tripId
  if (!open || !tripId) {
    return null;
  }

  // Helper function to format status badge
  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      PLANNED: "bg-blue-500",
      ACTIVE: "bg-yellow-500",
      COMPLETED: "bg-green-500",
      ARCHIVED: "bg-gray-500",
    };

    return (
      <Badge
        className={`${statusColors[status] || "bg-gray-500"}`}
        variant="default"
      >
        {status.replace("_", " ")}
      </Badge>
    );
  };

  // Helper function to format duration in hours and minutes
  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes}m`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (remainingMinutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${remainingMinutes}m`;
  };

  // Add a helper function to format order status badge colors consistently with orders-table.tsx
  const getOrderStatusColor = (status: string): string => {
    const statusColors: Record<string, string> = {
      REGISTERED: "bg-blue-500",
      PENDING: "bg-gray-500",
      PICKUP_PENDING: "bg-yellow-500",
      OUT_FOR_PICKUP: "bg-blue-500",
      PICKUP_ASSIGNED: "bg-blue-500",
      PICKED_UP: "bg-indigo-700",
      DEPOT_RECEIVE_PENDING: "bg-purple-500",
      DEPOT_RECEIVE_IN_PROGRESS: "bg-purple-500",
      AT_DEPOT: "bg-purple-500",
      DEPOT_SORT_PENDING: "bg-violet-500",
      DEPOT_SORT_IN_PROGRESS: "bg-violet-500",
      SORTED: "bg-violet-500",
      DEPOT_DISPATCH_PENDING: "bg-cyan-500",
      DEPOT_DISPATCH_IN_PROGRESS: "bg-cyan-500",
      IN_TRANSIT: "bg-cyan-500",
      DELIVERY_PENDING: "bg-yellow-500",
      OUT_FOR_DELIVERY: "bg-yellow-500",
      DELIVERED: "bg-green-700",
      RETURN_PENDING: "bg-orange-500",
      RETURN_IN_PROGRESS: "bg-orange-500",
      RETURNED: "bg-orange-500",
      PICKUP_FAILED: "bg-red-500",
      DEPOT_RECEIVE_FAILED: "bg-red-500",
      DEPOT_SORT_FAILED: "bg-red-500",
      DEPOT_DISPATCH_FAILED: "bg-red-500",
      DELIVERY_FAILED: "bg-red-500",
      RETURN_FAILED: "bg-red-500",
      FAILED: "bg-red-500",
      PICKUP_CANCELLED: "bg-red-700",
      DEPOT_RECEIVE_CANCELLED: "bg-red-700",
      DEPOT_SORT_CANCELLED: "bg-red-700",
      DEPOT_DISPATCH_CANCELLED: "bg-red-700",
      DELIVERY_CANCELLED: "bg-red-700",
      RETURN_CANCELLED: "bg-red-700",
      CANCELLED: "bg-red-700",
      PARTIALLY_PICKED_UP: "bg-indigo-500",
      PARTIALLY_DELIVERED: "bg-green-500",
      // Common simplified statuses for the UI
      Processing: "bg-yellow-500",
      Completed: "bg-green-700",
      Cancelled: "bg-red-700",
      Failed: "bg-red-500",
    };

    return statusColors[status] || "bg-gray-500";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] overflow-y-auto bg-gray-50 p-0 border-0">
        <DialogHeader className="bg-white p-6 rounded-t-lg border-b">
          <DialogTitle className="flex items-center gap-2 text-xl">
            {isLoading ? (
              <Skeleton className="h-6 w-48" />
            ) : (
              <>
                <span className="font-semibold">
                  Trip #{data?.tripInfo?.tripNumber ?? tripId.slice(0, 8)}
                </span>
                {data?.tripInfo?.status && getStatusBadge(data.tripInfo.status)}
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isLoading ? (
              <Skeleton className="h-4 w-64" />
            ) : (
              <>
                Created{" "}
                {data?.tripInfo?.createdAt &&
                  formatDistanceToNow(new Date(data.tripInfo.createdAt), {
                    addSuffix: true,
                  })}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-[100px] w-full" />
              ))}
            </div>
            <Skeleton className="h-[300px] w-full" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Metric Cards - First Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground mb-1">
                      Orders
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-3xl font-semibold">
                        {data?.analytics?.totalOrders ?? 0}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground mt-1">
                      Total orders
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground mb-1">
                      Parcels
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-3xl font-semibold">
                        {data?.analytics?.totalParcels ?? 0}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground mt-1">
                      Total parcels
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground mb-1">
                      Duration
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-3xl font-semibold">
                        {data?.analytics?.totalDuration
                          ? formatDuration(data.analytics.totalDuration)
                          : "-"}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground mt-1">
                      Total duration
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Metric Cards - Second Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground mb-1">
                      Pickups
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-3xl font-semibold">
                        {data?.analytics?.totalPickupTasks ?? 0}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground mt-1">
                      Pickup tasks
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground mb-1">
                      Deliveries
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-3xl font-semibold">
                        {data?.analytics?.totalDeliveryTasks ?? 0}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground mt-1">
                      Delivery tasks
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Task Status</CardTitle>
                  <CardDescription>Current task distribution</CardDescription>
                </CardHeader>
                <CardContent>
                  {progressQuery.isLoading ? (
                    <p className="text-sm text-muted-foreground">
                      Loading task progress…
                    </p>
                  ) : (
                    <TripProgress progress={progressQuery.data} />
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Order List */}
            <Card>
              <CardHeader>
                <CardTitle>Orders</CardTitle>
                <CardDescription>All orders in this trip</CardDescription>
              </CardHeader>
              <CardContent>
                {data?.orders && data.orders.length > 0 ? (
                  <div className="space-y-2">
                    {data.orders.map((order) => (
                      <div
                        key={order.id}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card text-card-foreground"
                      >
                        <div className="flex items-center gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">
                                ORD-{order.orderNumber || order.id.slice(0, 8)}
                              </span>
                              <Badge
                                className={getOrderStatusColor(
                                  order.paymentStatus || "N/A"
                                )}
                              >
                                {order.paymentStatus || "N/A"}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {getOrderRecipientInfo(order).name ||
                                "No recipient name"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center text-sm text-muted-foreground">
                          {getOrderRecipientInfo(order).address?.line1 ? (
                            <div className="flex items-center">
                              <MapPin className="h-4 w-4 mr-1" />
                              <span className="truncate max-w-[200px]">
                                {getOrderRecipientInfo(order).address?.line1}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center text-muted-foreground/60">
                              <MapPin className="h-4 w-4 mr-1" />
                              <span>No address</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 rounded-lg border border-dashed">
                    <Package className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No orders found
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
